/** Array access glue for geometry ports. Values stay in ordered PHP storage;
 * casts, copies, comparisons and array operations come from php-compat. */
import { PhpArray, PhpFloat, floatval, fromJson, type PhpKeyInput } from '../../php-compat';
import { portWritesBrand, type PortWithWrites, type PortWriteIntent } from '../../port-contract';

export const isArray = (value: unknown): value is object => value !== null
    && typeof value === 'object' && !(value instanceof PhpFloat);
export function array(value: unknown): PhpArray {
    return value instanceof PhpArray ? value : fromJson(value) as PhpArray;
}
export function at(value: unknown, ...keys: PhpKeyInput[]): unknown {
    for (const key of keys) {
        if (!isArray(value)) return null;
        value = array(value).get(key);
    }
    return value ?? null;
}
export const num = (value: unknown): number => Number(floatval(value));
export const f = (value: number): PhpFloat => new PhpFloat(value);
export const list = (values: unknown[]): PhpArray => new PhpArray(values.map((v, i) => [i, v]));
export const record = (entries: Record<string, unknown>): PhpArray => new PhpArray(Object.entries(entries));
export const values = (value: unknown): unknown[] => isArray(value) ? [...array(value).values()] : [];

/** PHP nested array assignment; callers copy by-value roots before using it. */
export function put(root: unknown, keys: PhpKeyInput[], value: unknown): void {
    let target = array(root);
    for (const key of keys.slice(0, -1)) {
        let child = target.get(key);
        if (!isArray(child)) { child = new PhpArray(); target.set(key, child); }
        target = array(child);
    }
    target.set(keys[keys.length - 1]!, value);
}

/** Canonicalize array reference boxes before writing; fromJson preserves the
 * native page shape's values and PHP key order, while writes keep their owner. */
export function arraysByRef(...refs: { value: unknown }[]): void {
    for (const ref of refs) ref.value = array(ref.value);
}

/** A cross-cluster rule may return persistence intents with its PHP value.
 * Keep those intents for the outer commit adapter, without performing I/O. */
export function collectWrites(result: unknown, writes: PortWriteIntent[]): unknown {
    const wrapped = result as PortWithWrites<unknown> | null | undefined;
    if (wrapped?.[portWritesBrand] === true) {
        writes.push(...wrapped.writes);
        return wrapped.value;
    }
    return result;
}

/** Function-local PHP statics live for one injected request, independently of
 * PHP globals/class properties. A fresh PortEnv starts a fresh request. */
export function functionStatic<T>(env: object, name: string, create: () => T): T {
    const request = env as { functionStatics?: Record<string, unknown> };
    const statics = request.functionStatics ??= Object.create(null) as Record<string, unknown>;
    const value = statics[name] ?? create();
    return (statics[name] = isArray(value) ? array(value) : value) as T;
}

/** Boundary adapter for an existing stateless geometry twin, never a row reader. */
export function native(value: unknown): unknown {
    if (value instanceof PhpFloat) return value.value;
    if (value instanceof PhpArray) {
        const entries = [...value];
        if (entries.every(([key], i) => key === i)) return entries.map(([, v]) => native(v));
        return Object.fromEntries(entries.map(([key, v]) => [key, native(v)]));
    }
    if (Array.isArray(value)) return value.map(native);
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, native(v)]));
    return value;
}
